import {
  useFlattenedGeometryShapes,
  usePortsGetList,
  useShapesList,
} from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Label,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  useStepperReturn,
} from '@ais-ui/ui-core';
import React, { useEffect, useMemo, useRef } from 'react';
import { StepperFooter } from './StepperFooter';
import { ShapeSelect } from '../../../Shapes/ShapeSelects';

import {
  ReportShapeItem,
  ShapeStructure,
  ShapeType,
  FilterType,
} from '../../reportsTypes';
import { ShapeMultiSelect } from '../../../Shapes/ShapeSelects';
import { createShapeDictionary } from '../utils/createShapeDictionary';
import { StepperHeadline } from './StepperHeadline';
import { useLocalization } from '@ais-ui/locale-management';
import { usePermissions } from '../../../Permissions/PermissionsProvider';
import { useCreateReportContext } from './CreateReportContext';
import { ReportTemplateShapeValues, useFormSetup } from './useDynamicShapeForm';
import { PortMultiSelect } from '../../../Shapes/ShapeSelects';
import { FormFieldTypes } from '../utils/formFieldTypes';
import { AdHocShapeSelect } from '../components/AdHocShapeSelect';
import { useInstantShapeAutoTrigger } from '../components/useInstantShapeAutoTrigger';
import {
  WORLD_SHAPE_FILTER_REQUIRED_MESSAGE,
  hasRequiredVesselSelection,
  type VesselSelection,
} from '../utils/worldShapeFilterRequirement';

interface ShapeFormProps {
  shapeItems: ReportShapeItem[];
  stepperProps: useStepperReturn;
}

interface MultiShapeFormProps {
  shapeItems: ReportShapeItem[];
  stepperProps: useStepperReturn;
}

interface PortFormProps {
  shapeItems: ReportShapeItem[];
  stepperProps: useStepperReturn;
}

interface WorldShapeFormProps {
  shapeItems: ReportShapeItem[];
  stepperProps: useStepperReturn;
}

interface SingleContentProps {
  shapeFields: ShapeStructure;
  stepperProps: useStepperReturn;
}

interface TabbedContentProps {
  shapeFields: ShapeStructure;
  stepperProps: useStepperReturn;
}

const ShapeLabel = ({ shapeItem }: { shapeItem: ReportShapeItem }) => {
  return (
    <div className="flex justify-between">
      <FormLabel>{shapeItem.label}: *</FormLabel>
      {shapeItem.help && (
        <InfoIconTooltip className="mr-2">{shapeItem.help}</InfoIconTooltip>
      )}
    </div>
  );
};

const ShapeForm = ({ shapeItems, stepperProps }: ShapeFormProps) => {
  const {
    shapeDictionary,
    setShapeDictionary,
    instantShapeData,
    instantShape,
    setInstantShape,
  } = useCreateReportContext();

  const { messages } = useLocalization();
  const currentShape: ShapeType = 'shape';
  const { shouldAutoTrigger, resetAutoTrigger } =
    useInstantShapeAutoTrigger(instantShape);

  // Raw shapes with feature collections intact for validation
  const { data: rawShapesData } = useShapesList();
  const shapesRef = useRef<GeoJson[] | undefined>(undefined);
  shapesRef.current = rawShapesData?.records ?? undefined;

  const form = useFormSetup(
    shapeItems,
    shapeDictionary,
    currentShape,
    instantShape ? instantShapeData : null,
    instantShape,
    shapesRef,
  );

  const { data: flattenedShapes } = useFlattenedGeometryShapes();

  // Combine flattened features with feature collections so
  // createShapeDictionary can find FCs by ID
  const shapes = useMemo(() => {
    if (!flattenedShapes) return undefined;
    const featureCollections = (rawShapesData?.records ?? []).filter(
      (s) => s.type === 'FeatureCollection',
    );
    return [...flattenedShapes, ...featureCollections];
  }, [flattenedShapes, rawShapesData]);

  // Update form values and shapeDictionary when instantShapeData changes
  React.useEffect(() => {
    if (
      instantShape &&
      instantShapeData &&
      Object.keys(instantShapeData).length > 0 &&
      shapes
    ) {
      // Set the form field values from instantShapeData
      Object.keys(instantShapeData).forEach((key) => {
        const geometry = instantShapeData[key];
        form.setValue(key, geometry);
      });

      // Manually create and set shapeDictionary since form.watch doesn't trigger on setValue
      const newShapeDictionary = createShapeDictionary(
        instantShapeData as Parameters<typeof createShapeDictionary>[0],
        shapes,
        true,
      );
      setShapeDictionary(newShapeDictionary);
    }
  }, [instantShapeData, instantShape, form, shapes, setShapeDictionary]);

  // Track when user explicitly toggles instant shape to true
  // (handled by useInstantShapeAutoTrigger above)

  // Sync form changes to context in real-time (like quick mode does)
  useEffect(() => {
    if (!form) {
      return;
    }

    const subscription = form.watch((values) => {
      if (!shapes || !values) {
        return;
      }

      const newShapeDictionary = createShapeDictionary(
        values as Parameters<typeof createShapeDictionary>[0],
        shapes,
        instantShape,
      );
      setShapeDictionary(newShapeDictionary);
    });

    return () => subscription.unsubscribe();
  }, [form, shapes, instantShape, setShapeDictionary]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = () => {
    // Shape dictionary is already synced via watch, just move to next step
    stepperProps.nextStep();
  };
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-[100%]"
      >
        <div className="h-[85%]">
          <div className="flex space-x-4">
            {shapeItems.map((shapeItem) => {
              const isFirstShapeItem = shapeItem.name === shapeItems[0].name;
              return (
                <FormField
                  key={shapeItem.name}
                  control={form.control}
                  name={shapeItem.name}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <div className="flex justify-between items-center mb-2">
                        <ShapeLabel shapeItem={shapeItem} />
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="report-shape-switch"
                            checked={instantShape}
                            onCheckedChange={(value) => {
                              setInstantShape(value);
                            }}
                          />
                          <Label className="text-sm">
                            {messages.reportsCreateReportShapeInstantShape}
                          </Label>
                          <InfoIconTooltip>
                            {
                              messages.reportsCreateReportShapeInstantShapeTooltip
                            }
                          </InfoIconTooltip>
                        </div>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        {!instantShape ? (
                          <ShapeSelect
                            id="shape-select-dropdown"
                            value={FormFieldTypes.asString(field)}
                            onChange={field.onChange}
                            allowedGeometryTypes={
                              shapeItem.allowedGeometryTypes
                            }
                            allowFeatureCollections={
                              shapeItem.allowFeatureCollections
                            }
                          />
                        ) : (
                          <AdHocShapeSelect
                            allowedGeometryTypes={
                              shapeItem.allowedGeometryTypes
                            }
                            geometryField={shapeItem.name}
                            form={form}
                            shouldAutoTrigger={
                              shouldAutoTrigger && isFirstShapeItem
                            }
                            onAutoTriggerComplete={resetAutoTrigger}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              );
            })}
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

const PortForm = ({ shapeItems, stepperProps }: PortFormProps) => {
  const { shapeDictionary, setShapeDictionary } = useCreateReportContext();

  // This might have to be determined from shapeItems if multiple ports should
  // be supported.
  const shapeItemKey: ShapeType = 'ports';

  const form = useFormSetup(shapeItems, shapeDictionary, shapeItemKey);
  const { data } = usePortsGetList();

  const ports = useMemo(() => data?.records || [], [data]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: ReportTemplateShapeValues) => {
    if (!ports) {
      return;
    }
    setShapeDictionary(data as Record<string, GeoJson>);

    stepperProps.nextStep();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            {shapeItems.map((shapeItem) => (
              <FormField
                key={shapeItem.name}
                control={form.control}
                name={shapeItem.name}
                render={({ field }) => (
                  <FormItem>
                    <ShapeLabel shapeItem={shapeItem} />
                    <FormControl onChange={() => form.clearErrors()}>
                      <PortMultiSelect
                        value={Array.isArray(field.value) ? field.value : []}
                        onChange={field.onChange}
                      ></PortMultiSelect>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

/**
 * Map the Wizard's polymorphic context filter onto the shared VesselSelection
 * contract. A `combined` filter is stored as a nested { shiplist, filter }
 * object; every other filter type stores its selection directly.
 */
function toVesselSelection(
  filterType: FilterType,
  filter: unknown,
): VesselSelection {
  if (
    filterType === 'combined' &&
    filter !== null &&
    typeof filter === 'object' &&
    !Array.isArray(filter)
  ) {
    const { shiplist, filter: expression } = filter as {
      shiplist?: unknown;
      filter?: unknown;
    };
    return { shiplist, filter: expression };
  }
  return { filter };
}

const WorldForm = ({ shapeItems, stepperProps }: WorldShapeFormProps) => {
  const { shapeDictionary, setShapeDictionary, filter, filterTab } =
    useCreateReportContext();
  const permissions = usePermissions();

  const shapeItemKey: ShapeType = 'worldShape';
  const form = useFormSetup(shapeItems, shapeDictionary, shapeItemKey);
  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: ReportTemplateShapeValues) => {
    // World reports have no area, so the backend needs a shiplist/ship selection
    // (chosen in the earlier Filter step). Enforce the SAME rule as the Quick
    // form so the two flows cannot drift apart.
    if (
      !hasRequiredVesselSelection(
        filterTab,
        toVesselSelection(filterTab, filter),
      )
    ) {
      shapeItems.forEach((shapeItem) => {
        form.setError(shapeItem.name as never, {
          type: 'manual',
          message: WORLD_SHAPE_FILTER_REQUIRED_MESSAGE,
        });
      });
      return;
    }
    setShapeDictionary(data as Record<string, GeoJson>);
    stepperProps.nextStep();
  };
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            {shapeItems.map((shapeItem) => (
              <FormField
                key={shapeItem.name}
                control={form.control}
                name={shapeItem.name}
                render={({ field }) => (
                  <FormItem className="flex flex-col space-y-4">
                    <ShapeLabel shapeItem={shapeItem} />
                    <FormControl onChange={() => form.clearErrors()}>
                      <Switch
                        disabled={!permissions.useWorldShape}
                        checked={FormFieldTypes.asBoolean(field)}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

const MultiShapeForm = ({ shapeItems, stepperProps }: MultiShapeFormProps) => {
  const { shapeDictionary, setShapeDictionary, activeTab } =
    useCreateReportContext();

  const shapeItemKey: ShapeType = 'multiShape';
  const form = useFormSetup(shapeItems, shapeDictionary, shapeItemKey);

  const { data: shapes } = useFlattenedGeometryShapes();

  // Sync form changes to context in real-time (like quick mode does)
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (!shapes || !values) return;

      const newShapeDictionary = createShapeDictionary(
        values as Parameters<typeof createShapeDictionary>[0],
        shapes,
      );
      setShapeDictionary(newShapeDictionary);
    });

    return () => subscription.unsubscribe();
  }, [form, shapes, setShapeDictionary]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = () => {
    if (!shapes) {
      return;
    }

    // Shape dictionary is already synced via watch, just move to next step
    stepperProps.nextStep();
  };
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            {shapeItems.map((shapeItem) => (
              <FormField
                key={shapeItem.name}
                control={form.control}
                name={shapeItem.name}
                render={({ field }) => (
                  <FormItem>
                    <ShapeLabel shapeItem={shapeItem} />
                    <FormControl onChange={() => form.clearErrors()}>
                      <ShapeMultiSelect
                        value={FormFieldTypes.asStringArray(field)}
                        onChange={field.onChange}
                        allowedGeometryTypes={shapeItem.allowedGeometryTypes}
                        allowFeatureCollections={
                          shapeItem.allowFeatureCollections
                        }
                      ></ShapeMultiSelect>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

const SingleContent = ({ shapeFields, stepperProps }: SingleContentProps) => {
  if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'shape') &&
    shapeFields.shape !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.shape;
    return <ShapeForm shapeItems={shapeItems} stepperProps={stepperProps} />;
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'multiShape') &&
    shapeFields.multiShape !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.multiShape;
    return (
      <MultiShapeForm
        stepperProps={stepperProps}
        shapeItems={shapeItems}
      ></MultiShapeForm>
    );
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'worldShape') &&
    shapeFields.worldShape !== undefined
  ) {
    return;
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'ports') &&
    shapeFields.ports !== undefined
  ) {
    return;
  }
};

const TabbedContent = ({ shapeFields, stepperProps }: TabbedContentProps) => {
  const { messages } = useLocalization();
  const { shapeTab, setShapeTab } = useCreateReportContext();
  const permissions = usePermissions();

  const shapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'shape')) {
      return shapeFields.shape;
    }
    return null; // or a default value
  }, [shapeFields]);

  const multiShapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'multiShape')) {
      return shapeFields.multiShape;
    }
    return null; // or a default value
  }, [shapeFields]);

  const portItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'ports')) {
      return shapeFields.ports;
    }
    return null; // or a default value
  }, [shapeFields]);

  const worldShapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'worldShape')) {
      return shapeFields.worldShape;
    }
    return null; // or a default value
  }, [shapeFields]);

  return (
    <Tabs
      value={shapeTab}
      onValueChange={(value) => setShapeTab(value as ShapeType)}
      defaultValue="shape"
      className="w-full space-y-4 pt-4 h-full flex flex-col"
    >
      <TabsList className="w-fit">
        {shapeItems && (
          <TabsTrigger value="shape">
            {messages.reportsCreateReportShapeTabShape}
          </TabsTrigger>
        )}
        {multiShapeItems && (
          <TabsTrigger value="multiShape">
            {messages.reportsCreateReportShapeTabMultiShape}
          </TabsTrigger>
        )}
        {portItems && (
          <TabsTrigger value="ports">
            {messages.reportsCreateReportShapeTabPort}
          </TabsTrigger>
        )}
        {worldShapeItems && (
          <TabsTrigger value="worldShape" disabled={!permissions.useWorldShape}>
            {messages.reportsCreateReportShapeTabWorld}
          </TabsTrigger>
        )}
      </TabsList>
      {shapeItems && shapeTab === 'shape' && (
        <ShapeForm shapeItems={shapeItems} stepperProps={stepperProps} />
      )}
      {multiShapeItems && shapeTab === 'multiShape' && (
        <MultiShapeForm
          shapeItems={multiShapeItems}
          stepperProps={stepperProps}
        />
      )}
      {portItems && shapeTab === 'ports' && (
        <PortForm shapeItems={portItems} stepperProps={stepperProps} />
      )}
      {worldShapeItems && shapeTab === 'worldShape' && (
        <WorldForm shapeItems={worldShapeItems} stepperProps={stepperProps} />
      )}
    </Tabs>
  );
};

export const ShapeStep = () => {
  const { messages } = useLocalization();

  const { stepperProps, chosenReportType } = useCreateReportContext();

  const shapeFields = useMemo(() => {
    return chosenReportType?.shape as ShapeStructure;
  }, [chosenReportType]);

  return (
    <div className="grid grid-rows-[auto_1fr_auto] h-full">
      <StepperHeadline
        headerText={messages.reportsCreateReportShapeTitle}
        subHeaderText={messages.reportsCreateReportShapeSubtitle}
      ></StepperHeadline>
      {shapeFields && Object.keys(shapeFields).length === 1 && (
        <SingleContent
          stepperProps={stepperProps}
          shapeFields={shapeFields}
        ></SingleContent>
      )}
      {shapeFields && Object.keys(shapeFields).length > 1 && (
        <TabbedContent
          stepperProps={stepperProps}
          shapeFields={shapeFields}
        ></TabbedContent>
      )}
    </div>
  );
};
